import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';

// models
import { BarChartSettings } from '../../models/barChartSettings';
import { BAR_CHART_TEMPLATE_DATA } from '../../models/barChartTemplateData';
import { IBarChartParam, IBarChartParamValue } from '../../models/barChartParams';
import { IBarChartSettingsValue } from '../../models/barChartSettingsValue';
import { IChartData } from '../../models/chartData';
import { MixpanelEvents } from '../../models/mixpanelEvents';
import { Chart, SeriesOptionsType } from 'highcharts';

// components
import {
	BarChartSettingsModalComponent
} from '../../modals/bar-chart-settings-modal/bar-chart-settings-modal.component';

// services
import { MixpanelService } from '../../services/mixpanel.service';
import { SessionService } from '../../services/session.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { Utils } from '../../services/utils.service';

import { SHARED_MODULES } from '../../../shared.';
import { LoaderComponent } from '../loader/loader.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { HighchartsChartComponent } from 'highcharts-angular';

const CHART_FILE_WIDTH: number = 1700;

@Component({
    selector: 'app-ph-bar-chart',
    templateUrl: './ph-bar-chart.component.html',
    styleUrls: ['./ph-bar-chart.component.scss'],
	imports: [SHARED_MODULES, HighchartsChartComponent, LoaderComponent, PhIconComponent, PhIconWrapperComponent],
    standalone: true
})
export class PhBarChartComponent implements OnInit, OnDestroy {

	chart: Chart;
	data: IChartData;
	Highcharts: typeof Highcharts = Highcharts;
	options: Highcharts.Options;
	settings: BarChartSettings;
	showLoader: boolean = false;
	translates: { period: string; prevPeriod: string };
	private chartRedrawTimeout: ReturnType<typeof setTimeout> | null = null;

	@Input() settingsKey: string;
	@Input() params: Array<IBarChartParam>;
	@Input() disableOptions: boolean = false;
	@Input('data') set setData(data: IChartData) {
		this.data = data || BAR_CHART_TEMPLATE_DATA;
		if (this.chart) this.setChart();
	}
	@Input('settings') set setSettings(value: BarChartSettings) {
		this.settings = value;
		if (this.chart) this.setChart();
	};

	@Output('settingsUpdate') onSettingsUpdateEmitter: EventEmitter<IBarChartSettingsValue> = new EventEmitter<IBarChartSettingsValue>();
	@Output('onDownload') onDownloadEmitter: EventEmitter<void> = new EventEmitter<void>();

	constructor(
		private dialog: MatDialog,
		private translate: TranslateService,
		private mixpanelService: MixpanelService,
		private sessionService: SessionService,
		private snackBarService: SnackBarService
	) {
        this.translates = {
            period: this.translate.instant('PERIOD'),
            prevPeriod: this.translate.instant('PREV_PERIOD')
        }
	}

	private _buildChart(): void {
		const series: Array<SeriesOptionsType> = [
			{
				name: this.translates.period,
				type: 'column',
				data: this.data.values,
				color: this.settings.mainColor,
				dataLabels: {
					enabled: true,
					style: {
						fontWeight: '400'
					}
				}
			}
		];

		if (this.data.prev_values?.length) {
			series.push({
				name: this.translates.prevPeriod,
				type: 'column',
				data: this.data.prev_values,
				color: this.settings.secondaryColor,
				dataLabels: {
					enabled: true,
					style: {
						fontWeight: '400'
					}
				}
			});
		}

		this.options = {
            lang: {
                decimalPoint: '.',
                thousandsSep: ' '
            },
			chart: {
				type: 'column',
				style: {
					fontFamily: '\'Roboto Condensed\', sans-serif'
				},
			},
			title: {
				text: this.data.hide_title ? '' : this.data.title,
				align: 'left'
			},
			exporting: {
				enabled: false,
                chartOptions: {
                    chart: {
                        style: { fontFamily: '\'Roboto Condensed\', sans-serif' }
                    }
                }
			},
			accessibility: {
				enabled: false
			},
			legend: {
				verticalAlign: 'top',
				align: 'left',
				itemMarginBottom: 10,
                itemHiddenStyle: {
                    textDecoration: 'none'
                }
			},
			credits: {
				enabled: false
			},
			xAxis: {
				categories: this.data.categories,
				min: 0,
				max: Math.min(this.settings.count, this.data.categories.length) - 1,
				labels: {
					style: {
						color: '#000000'
					},
				},
				title: {
					text: this.data.x_title,
					margin: 20,
					style: {
						color: '#a7a7a7',
						fontWeight: 'bold'
					}
				}
			},
			yAxis: {
				title: {
					text: this.data.y_title,
					style: {
						color: '#a7a7a7',
						fontWeight: 'bold'
					}
				}
			},
			tooltip: {
				animation: false,
				hideDelay: 0,
				formatter: function() {
					const headerFormat: string = `<p style="margin: 0 0 10px 0"><b>${this.category}</b></p>`,
						columnsFormat: string = this.points
							.map((point: Highcharts.TooltipPositionerPointObject, index: number) =>
								`<div style="margin: 0; display: flex; align-items: center; column-gap: 5px">
									<span style="display: inline-block; width: 5px; height: 5px; border-radius: 50%; background-color: ${point.color}"></span>
									<span>${point.series.yAxis.options.title.text}${index ? ' (' + point.series.name + ')' : ''}: ${point.y}</span>
								</div>`
							)
							.join(' ');

					return headerFormat + columnsFormat;
				},
				borderWidth: 0,
				backgroundColor: '#FFFFFF',
				useHTML: true,
				shared: true
			},
			plotOptions: {
				column: {
					pointPadding: 0,
					maxPointWidth: 22,
					borderWidth: 0,
					states: {
						hover: {
							enabled: false,
						}
					}
				}
			},
			series: series
		};
	}

	ngOnInit(): void {
		if (!this.settings) this.settings = new BarChartSettings(this.sessionService.settings[this.settingsKey]);
		this.setChart();
	}

	exportChart(): void {
		this.chart.exporting.exportChart({
			type: 'image/png',
			local: true,
			filename: this.data.title,
			sourceWidth: CHART_FILE_WIDTH
		}, this.options);

		this.mixpanelService.trackPage(MixpanelEvents.BarChartDownload);
		this.onDownloadEmitter.emit();
	}

	getChartInstance(chart: Chart): void {
		if (!chart.renderer.forExport) this.chart = chart;
	}

	getSvgImage(): string {
		// set a fixed width of the chat for convenient display of the picture in the file
		this.options.chart.width = CHART_FILE_WIDTH;
		return Utils.svgToBase64(this.chart.exporting.getSVG(this.options));
	}

	onParamChange(param: IBarChartParam, value: IBarChartParamValue): void {
		param.control.setValue(value);
	}

	openSettingsModal(): void {
		const dialogRef: MatDialogRef<BarChartSettingsModalComponent> = this.dialog.open(BarChartSettingsModalComponent, {
			width: '1200px',
			autoFocus: false,
			backdropClass: 'back-drop',
			panelClass: 'scrollable-content',
			data: {
				chartName: this.data.title,
				settings: this.settings,
				metrics: this.params[0].values
			}
		});

		dialogRef.afterClosed().subscribe((result: IBarChartSettingsValue) => {
			if (!result) return;

			const needRequest: boolean =
				result.count !== this.settings.count ||
				result.defaultMetric !== this.settings.defaultMetric;

			this.settings = new BarChartSettings(result);
			if (!needRequest) this.setChart();
			this.sessionService.defaults.settings[this.settingsKey] = result;
			this.sessionService.updateSettings$().subscribe({
				next: () => {
					if (needRequest) this.onSettingsUpdateEmitter.emit(result);
				},
				error: () => {
					this.snackBarService.setErrorNotification();
					this.showLoader = false;
				}
			});
		});
	}

	setChart(): void {
		this._buildChart();
        if (this.chart) this.chart.update(this.options, true, true);
		this.showLoader = false;
	}

	ngOnDestroy(): void {
        this.options = null;
        this.chart = null;
		if (this.chartRedrawTimeout) {
			clearTimeout(this.chartRedrawTimeout);
			this.chartRedrawTimeout = null;
		}
	}

	startLoading(): void {
		this.showLoader = true;
	}
}
